import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { Line, Bar } from 'react-chartjs-2';
import { Chart } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import DeleteIcon from '@mui/icons-material/Delete';
import Input from '@mui/material/Input';
import Carousel from 'react-multi-carousel';
import { RotatingLines } from "react-loader-spinner";
import { motion } from "framer-motion";
import { Lock, Unlock } from "lucide-react";
import { format } from 'date-fns';
import Cookies from 'js-cookie';
import axios from 'axios';
import { color } from 'highcharts';

import 'chart.js/auto';
import 'react-multi-carousel/lib/styles.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// Register the plugin
Chart.register(annotationPlugin);

// ==========================================
// CONSTANTS & UTILS
// ==========================================

const API_URL = `https://options.healthsaving.life/api/`;

// Utility to generate unique colors
function generateUniqueColors(count) {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = (i * 137.5) % 360; // Distribute hues evenly
    colors.push(`hsl(${hue}, 70%, 50%)`); // Use HSL for vivid colors
  }
  return colors;
}

// ==========================================
// API FUNCTIONS
// ==========================================

// Fetch acct_names from the API
const fetchacct_names = async () => {
  let api_url = `${API_URL}get_option_chain_ibkr_positions_acct_name.php`;
  const response = await fetch(`${api_url}`);
  const data = await response.json();
  return data.data.map(item => item.acct_name);
};

// Fetch symbols from the API
const fetchSymbols = async (portfolioMode, selectedacct_name) => {
  let api_url = `${API_URL}get_option_chain_ibkr_positions_symbol.php`;

  if (portfolioMode) {
    api_url = `${API_URL}get_option_chain_ibkr_positions_symbol.php?acct_name=${selectedacct_name}`;
  }

  const response = await fetch(`${api_url}`);
  const data = await response.json();
  return data.data.map(item => item.symbol);
};

// Fetch expiry dates based on the selected symbol
const fetchExpiries = async (symbol, portfolioMode, selectedacct_name) => {
  if (symbol) {
    let api_url = `${API_URL}get_option_chain_ibkr_positions_expiry.php?symbol=${symbol}`;
    if (portfolioMode) {
      api_url = `${API_URL}get_option_chain_ibkr_positions_expiry.php?symbol=${symbol}&acct_name=${selectedacct_name}`;
    }

    const response = await fetch(`${api_url}`);
    const data = await response.json();
    if (data) {
      return data;
    } else {
      return [];
    }
  } else {
    return [];
  }
};

// Fetch strike price range and data
const fetchStrikePriceRange = async (symbol, expiry, portfolioMode, selectedacct_name) => {
  if (symbol) {
    let api_url = `${API_URL}get_option_chain_ibkr_positions_data.php?symbol=${symbol}`;

    if (portfolioMode) {
      api_url = `${API_URL}get_option_chain_ibkr_positions_data.php?symbol=${symbol}&acct_name=${selectedacct_name}`;

      if (expiry) {
        api_url = `${API_URL}get_option_chain_ibkr_positions_data.php?symbol=${symbol}&expiry=${expiry}&acct_name=${selectedacct_name}`;
      }
    }

    const response = await fetch(`${api_url}`);
    const data = await response.json();
    return data.data;

  } else {
    return [];
  }
};

// Fetch portfolio data from the API
const fetchPortfolio = async (symbol, expiry, selectedacct_name) => {
  if (symbol) {
    const response = await fetch(`${API_URL}get_option_chain_ibkr_positions.php?symbol=${symbol}&expiry=${expiry}&acct_name=${selectedacct_name}`);
    const data = await response.json();
    if (data.result) {
      return data.data[0];
    } else {
      return { symbol: '', expiry: '', strikes: '[{"type":"Buy","values":[]},{"type":"Sell","values":[]}]', underlying_data: [], avg_price_data: [] }
    }
  } else {
    return { symbol: '', expiry: '', strikes: '[{"type":"Buy","values":[]},{"type":"Sell","values":[]}]', underlying_data: [], avg_price_data: [] }
  }
};

// Function to simulate fetching strike prices based on symbol and expiry
const fetchStrikePrices = (symbol, expiry) => {
  return [
    { type: 'Buy', values: [] },
    { type: 'Sell', values: [] },
  ];
};

// ==========================================
// MATH & FINANCIAL MODELS
// ==========================================

// Helper function to calculate the cumulative normal distribution function (CDF)
const cumulativeNormalDistribution = (x) => {
  const p = 0.2316419;
  const b1 = 0.319381530;
  const b2 = -0.356563782;
  const b3 = 1.781477937;
  const b4 = -1.821255978;
  const b5 = 1.330274429;

  // Handle the symmetry for negative values of x
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);

  const t = 1 / (1 + p * x);
  const poly = (((((b5 * t + b4) * t + b3) * t + b2) * t + b1) * t);
  const d = 1 - (Math.exp(-x * x / 2) / Math.sqrt(2 * Math.PI)) * poly;

  return sign === 1 ? d : 1 - d;
};

// Black-Scholes pricing function
const calculateBlackScholes = (S, K, T, r, sigma, type) => {
  const d1 = (Math.log(S / K) + (r + (sigma ** 2) / 2) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);

  const callPrice = S * cumulativeNormalDistribution(d1) - K * Math.exp(-r * T) * cumulativeNormalDistribution(d2);
  const putPrice = K * Math.exp(-r * T) * cumulativeNormalDistribution(-d2) - S * cumulativeNormalDistribution(-d1);

  // Check if any calculation results in NaN and return 0 if so
  if (isNaN(callPrice) || isNaN(putPrice)) {
    return 0;
  }

  return type === 'call' ? callPrice : putPrice;
};

// ==========================================
// DATA GENERATION & METRICS
// ==========================================

// Function to generate option data for the graph
const generateOptionsData = (stockPrice, minPrice, maxPrice, strikes, allStrikes, displayAsPercentage, contractSize, date, impliedVolatility, underlyingData, NewriskFreeRate, thumbMoved, selectedSymbol) => {
  if (true) {
    const data = [];
    const range_diff = maxPrice - minPrice;
    const points = 200;
    let step = range_diff / points;
    if (step < 0.1) step = 0.1;
    const premiumReceived = 0;

    let optionPrice = 0;
    let optionValue1 = 0; let optionValue2 = 0; let optionValue3 = 0;
    let optionValue4 = 0; let optionValue5 = 0; let optionValue6 = 0;

    let PD1 = 0; let PD2 = 0; let PD3 = 0; let PD4 = 0;

    let oV1 = 0; let oV2 = 0; let oV3 = 0; let oV4 = 0; let oV5 = 0; let oV6 = 0;

    let bs1 = 0; let bs2 = 0; let bs3 = 0; let bs4 = 0;

    let all_optionPrice = 0;
    let all_optionValue1 = 0; let all_optionValue2 = 0; let all_optionValue3 = 0;
    let all_optionValue4 = 0; let all_optionValue5 = 0; let all_optionValue6 = 0;

    let all_PD1 = 0; let all_PD2 = 0; let all_PD3 = 0; let all_PD4 = 0;

    let all_oV1 = 0; let all_oV2 = 0; let all_oV3 = 0; let all_oV4 = 0;
    let all_oV5 = 0; let all_oV6 = 0;

    let all_bs1 = 0; let all_bs2 = 0; let all_bs3 = 0; let all_bs4 = 0;

    let riskFreeRate = NewriskFreeRate / 100; //0.0379
    let impliedVolatilityPer = impliedVolatility / 100;
    let data_push = false;
    let equity_draw = false;

    for (let stockPrice = minPrice; stockPrice <= maxPrice; stockPrice += step) {
      let payoff = premiumReceived;
      let all_payoff = premiumReceived;
      let premium_deployed = 0;
      let equity = 0;

      allStrikes.forEach((strike) => {
        if (strike.symbol == selectedSymbol) {
          data_push = true;

          const lotMultiplier = strike.quantity ?? 1;
          const avg_price_share = (thumbMoved) ? strike.avg_price_share_new : strike.avg_price_share;
          const shares = lotMultiplier * contractSize;
          const strike_expiry = strike.expiry ?? 0;
          const strike_value = strike.value ?? 0;
          const strike_iv = (impliedVolatilityPer === 0) ? strike.iv : impliedVolatilityPer;

          const date1Ms = new Date(strike_expiry).getTime();
          const date2Ms = new Date().getTime();
          const differenceMs = Math.abs(date2Ms - date1Ms);
          const differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
          let T = (differenceDays - date) / 365;

          if (strike.type === 'Buy Call') {
            all_PD1 = all_PD1 + shares * avg_price_share;
            if (date === 0) {
              all_optionPrice = (stockPrice > strike_value) ? stockPrice - strike_value : 0;
              all_oV1 = shares * (all_optionPrice - avg_price_share);
              all_optionValue1 = all_optionValue1 + all_oV1;
              all_oV1 = 0;
            } else {
              all_bs1 = calculateBlackScholes(stockPrice, strike_value, T, riskFreeRate, strike_iv, 'call');
              all_oV1 = shares * (all_bs1 - avg_price_share);
              all_optionValue1 = all_optionValue1 + all_oV1;
              all_oV1 = 0;
            }
          } else if (strike.type === 'Buy Put') {
            all_PD2 = all_PD2 + shares * avg_price_share;
            if (date === 0) {
              all_optionPrice = (strike_value > stockPrice) ? strike_value - stockPrice : 0;
              all_oV2 = shares * (all_optionPrice - avg_price_share);
              all_optionValue2 = all_optionValue2 + all_oV2;
              all_oV2 = 0;
            } else {
              all_bs2 = calculateBlackScholes(stockPrice, strike_value, T, riskFreeRate, strike_iv, 'put');
              all_oV2 = shares * (all_bs2 - avg_price_share);
              all_optionValue2 = all_optionValue2 + all_oV2;
              all_oV2 = 0;
            }
          } else if (strike.type === 'Sell Call') {
            all_PD3 = all_PD3 + shares * avg_price_share;
            if (date === 0) {
              all_optionPrice = (stockPrice > strike_value) ? stockPrice - strike_value : 0;
              all_oV3 = shares * (all_optionPrice - avg_price_share);
              all_optionValue3 = all_optionValue3 + all_oV3;
              all_oV3 = 0;
            } else {
              all_bs3 = calculateBlackScholes(stockPrice, strike_value, T, riskFreeRate, strike_iv, 'call');
              all_oV3 = shares * (all_bs3 - avg_price_share);
              all_optionValue3 = all_optionValue3 + all_oV3;
              all_oV3 = 0;
            }
          } else if (strike.type === 'Sell Put') {
            all_PD4 = all_PD4 + shares * avg_price_share;
            if (date === 0) {
              all_optionPrice = (strike_value > stockPrice) ? strike_value - stockPrice : 0;
              all_oV4 = shares * (all_optionPrice - avg_price_share);
              all_optionValue4 = all_optionValue4 + all_oV4;
              all_oV4 = 0;
            } else {
              all_bs4 = calculateBlackScholes(stockPrice, strike_value, T, riskFreeRate, strike_iv, 'put');
              all_optionValue4 = shares * (all_bs4 - avg_price_share);
              all_oV4 = all_optionValue4 + all_oV4;
              all_oV4 = 0;
            }
          }
        }
      });

      strikes.forEach((strike) => {
        if (strike.symbol == selectedSymbol) {
          data_push = true;

          const lotMultiplier = strike.quantity ?? 1;
          const avg_price_share = (thumbMoved) ? strike.avg_price_share_new : strike.avg_price_share;
          const shares = lotMultiplier * contractSize;
          const strike_expiry = strike.expiry ?? 0;
          const strike_value = strike.value ?? 0;
          const strike_iv = (impliedVolatilityPer === 0) ? strike.iv : impliedVolatilityPer;

          const date1Ms = new Date(strike_expiry).getTime();
          const date2Ms = new Date().getTime();
          const differenceMs = Math.abs(date2Ms - date1Ms);
          const differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
          let T = (differenceDays - date) / 365;

          if (strike.type === 'Buy Call') {
            PD1 = PD1 + shares * avg_price_share;
            if (date === 0) {
              optionPrice = (stockPrice > strike_value) ? stockPrice - strike_value : 0;
              oV1 = shares * (optionPrice - avg_price_share);
              optionValue1 = optionValue1 + oV1;
              oV1 = 0;
            } else {
              bs1 = calculateBlackScholes(stockPrice, strike_value, T, riskFreeRate, strike_iv, 'call');
              oV1 = shares * (bs1 - avg_price_share);
              optionValue1 = optionValue1 + oV1;
              oV1 = 0;
            }
          } else if (strike.type === 'Buy Put') {
            PD2 = PD2 + shares * avg_price_share;
            if (date === 0) {
              optionPrice = (strike_value > stockPrice) ? strike_value - stockPrice : 0;
              oV2 = shares * (optionPrice - avg_price_share);
              optionValue2 = optionValue2 + oV2;
              oV2 = 0;
            } else {
              bs2 = calculateBlackScholes(stockPrice, strike_value, T, riskFreeRate, strike_iv, 'put');
              oV2 = shares * (bs2 - avg_price_share);
              optionValue2 = optionValue2 + oV2;
              oV2 = 0;
            }
          } else if (strike.type === 'Sell Call') {
            PD3 = PD3 + shares * avg_price_share;
            if (date === 0) {
              optionPrice = (stockPrice > strike_value) ? stockPrice - strike_value : 0;
              oV3 = shares * (optionPrice - avg_price_share);
              optionValue3 = optionValue3 + oV3;
              oV3 = 0;
            } else {
              bs3 = calculateBlackScholes(stockPrice, strike_value, T, riskFreeRate, strike_iv, 'call');
              oV3 = shares * (bs3 - avg_price_share);
              optionValue3 = optionValue3 + oV3;
              oV3 = 0;
            }
          } else if (strike.type === 'Sell Put') {
            PD4 = PD4 + shares * avg_price_share;
            if (date === 0) {
              optionPrice = (strike_value > stockPrice) ? strike_value - stockPrice : 0;
              oV4 = shares * (optionPrice - avg_price_share);
              optionValue4 = optionValue4 + oV4;
              oV4 = 0;
            } else {
              bs4 = calculateBlackScholes(stockPrice, strike_value, T, riskFreeRate, strike_iv, 'put');
              optionValue4 = shares * (bs4 - avg_price_share);
              oV4 = optionValue4 + oV4;
              oV4 = 0;
            }
          }
        }
      });

      if (Array.isArray(underlyingData)) {
        underlyingData.forEach((underlying) => {
          const avg_price_share = underlying.avg_price_share ?? 0;
          const underlying_shares = underlying.shares ?? 0;

          if (underlying.side === 'B') {
            oV5 = underlying_shares * (stockPrice - avg_price_share);
            optionValue5 = optionValue5 + oV5;
            oV5 = 0;
          } else if (underlying.side === 'S') {
            oV6 = underlying_shares * (stockPrice - avg_price_share);
            optionValue6 = optionValue6 + oV6;
            oV6 = 0;
          }
          data_push = true;
        });
      } else {
        console.error("underlyingData is not an array:", underlyingData);
      }

      payoff = optionValue1 + optionValue2 - optionValue3 - optionValue4 + optionValue5 + optionValue6;
      all_payoff = all_optionValue1 + all_optionValue2 - all_optionValue3 - all_optionValue4 + optionValue5 + optionValue6;

      premium_deployed = PD3 + PD4 - PD1 - PD2;

      equity = optionValue5 - optionValue6;
      if (equity != 0) {
        equity_draw = true;
      }

      if (data_push) {
        data.push({
          stockPrice,
          payoff: payoff,
          all_payoff: all_payoff,
          premium_deployed: premium_deployed,
          symbol: selectedSymbol,
          equity: equity,
          equity_draw: equity_draw,
        });
      }

      payoff = 0; premium_deployed = 0;
      optionValue1 = 0; optionValue2 = 0; optionValue3 = 0; optionValue4 = 0; optionValue5 = 0; optionValue6 = 0;
      PD1 = 0; PD2 = 0; PD3 = 0; PD4 = 0;
      all_optionValue1 = 0; all_optionValue2 = 0; all_optionValue3 = 0; all_optionValue4 = 0; all_optionValue5 = 0; all_optionValue6 = 0;
      all_PD1 = 0; all_PD2 = 0; all_PD3 = 0; all_PD4 = 0;
    }
    return data;
  } else {
    return [];
  }
};

// Function to calculate metrics for the options strategy
const calculateMetrics = (strikes = [], impliedVolatility, date) => {
  const optionPremiums = { 'Buy Call': 5, 'Sell Call': 5, 'Buy Put': 5, 'Sell Put': 5 };
  let totalDebit = 0;
  let totalCredit = 0;

  strikes.forEach((strike) => {
    if (strike.values && Array.isArray(strike.values)) {
      strike.values.forEach((value) => {
        const premium = (optionPremiums[value.type] ?? 0) * (value.quantity ?? 1);
        if (value.type.includes('Buy')) {
          totalDebit += premium;
        } else if (value.type.includes('Sell')) {
          totalCredit += premium;
        }
      });
    }
  });

  const netDebit = totalDebit - totalCredit;
  const maxLoss = Math.max(totalDebit, 0);
  const maxProfit = Math.max(totalCredit, 0);
  const chanceOfProfit = '50%';

  const allStrikeValues = strikes.flatMap((s) =>
    Array.isArray(s.values) ? s.values.map((v) => v.value ?? 0) : []
  );
  const minStrike = allStrikeValues.length > 0 ? Math.min(...allStrikeValues) : 0;
  const maxStrike = allStrikeValues.length > 0 ? Math.max(...allStrikeValues) : 0;
  const breakEven = (minStrike + maxStrike) / 2;

  return { netDebit, maxLoss, maxProfit, chanceOfProfit, breakEven };
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const OptionsGraphWithMultiStrikeSlider = () => {
  // 1. REFS & ROUTER
  const checkFirstRender1 = useRef(true);
  const checkFirstRender2 = useRef(true);
  const sliderRef = useRef();
  const textContentRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParamsReact, setSearchParams] = useSearchParams();

  // 2. STATE DEFINITIONS
  // Data State
  const [symbols, setSymbols] = useState([]);
  const [expiries, setExpiries] = useState([]);
  const [allExpiries, setAllExpiries] = useState([]);
  const [expiryColors, setExpiryColors] = useState({});
  const [maxExpiry, setMaxExpiry] = useState(null);
  const [maxDays, setMaxDays] = useState(30);
  const [strikes, setStrikes] = useState([]);
  const [strikesChange, setStrikesChange] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [acct_names, setacct_names] = useState(['Walleyhouse']);
  const [selectedacct_name, setSelectedacct_name] = useState('Walleyhouse');
  const [selectedExpiry, setSelectedExpiry] = useState([]);
  const [range, setRange] = useState([0, 100]);
  const [sliderRange, setSliderRange] = useState(1);
  const [optionsStrikes, setOptionsStrikes] = useState([]);
  const [selectedStrike, setSelectedStrike] = useState('');
  const [underlyingData, setunderlyingData] = useState([]);
  const [avgPriceData, setavgPriceData] = useState([]);
  const [datasets, setDatasets] = useState([]);

  // Numerical/Math State
  const [minStrikePrice, setMinStrikePrice] = useState(0);
  const [maxStrikePrice, setMaxStrikePrice] = useState(0);
  const [strike_range, setstrike_range] = useState(0);
  const [stockPrice, setStockPrice] = useState(0);
  const [stockPriceCMP, setStockPriceCMP] = useState(0);
  const [impliedVolatility, setImpliedVolatility] = useState(0);
  const [riskFreeRate, setriskFreeRate] = useState(3.79);
  const [date, setDate] = useState(0);
  const [debouncedDate, setDebouncedDate] = useState(date);
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(0);
  const [max_profit, setmax_profit] = useState(0);
  const [premium_deployed, setpremium_deployed] = useState(0);
  const [contractSize, setContractSize] = useState(100);

  // UI State
  const [portfolioMode, setPortfolioMode] = useState(true);
  const [hideExpiry, setHideExpiry] = useState('block');
  const [hideACCName, setHideACCName] = useState('block');
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [loading, setLoading] = useState(false);
  const [thumbMoved, setThumbMoved] = useState(false);
  const [isLocked, setIsLocked] = useState(true);
  const [sliderOverPad, setsliderOverPad] = useState(40);
  const [textContentRefLeft, settextContentRefLeft] = useState(5);
  const [displayAsPercentage, setDisplayAsPercentage] = useState(false);

  // Dragging State
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [isThumbDragging, setIsThumbDragging] = useState(false);

  // Popup & Modal State
  const [anchorEl, setAnchorEl] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showPopupUnderlying, setShowPopupUnderlying] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [popupData, setPopupData] = useState('');

  // Auth State
  const [show, setShow] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');

  // Detailed State (Greeks, Types, Inputs)
  const [currentTypeIndex, setCurrentTypeIndex] = useState(null);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(null);
  const [currentValueIndex, setCurrentValueIndex] = useState(null);
  const [currentStrikeValue, setCurrentStrikeValue] = useState(0);
  const [currentStrikePrice, setCurrentStrikePrice] = useState(0);
  const [currentStriketype, setCurrentStriketype] = useState(0);
  const [currentStrikeExpiry, setCurrentStrikeExpiry] = useState(0);
  const [currentStrikeavg_price_share, setCurrentStrikeavg_price_share] = useState(0);
  const [price, setPrice] = useState(0);

  const [bidprice, setBidPrice] = useState(0);
  const [askprice, setAskPrice] = useState(0);
  const [volume, setVolume] = useState(0);
  const [oi, setOI] = useState(0);
  const [iv, setIV] = useState(0);
  const [delta, setDelta] = useState(0);
  const [theta, setTheta] = useState(0);
  const [gamma, setGamma] = useState(0);
  const [vega, setVega] = useState(0);
  const [rho, setRho] = useState(0);

  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [editValues, setEditValues] = useState({ shares: '', avg_price_share: '' });

  const [optionsSelectedExpiry, setOptionsSelectedExpiry] = useState('');
  const [popupTypeIndex, setPopupTypeIndex] = useState(null);
  const [popupStrikeType, setPopupStrikeType] = useState('');

  const [popupShares, setPopupShares] = useState(100);
  const [popupAvgPriceShare, setPopupAvgPriceShare] = useState(stockPrice);
  const [popupTypeUnderlying, setPopupTypeUnderlying] = useState('');

  // Filters State
  const filterDropdownItems = [
    { type: 'Buy Call', label: 'Long', labelType: 'Call' },
    { type: 'Sell Call', label: 'Short', labelType: 'Call' },
    { type: 'Buy Put', label: 'Long', labelType: 'Put' },
    { type: 'Sell Put', label: 'Short', labelType: 'Put' },
  ];

  const [checkedItems, setCheckedItems] = useState(
    filterDropdownItems.reduce((acc, _, idx) => {
      acc[idx] = true;
      return acc;
    }, {})
  );

  const [barGraphFilter, setBarGraphFilter] = useState('Call');

  let timeoutId = null; // Used in handleSliderChange

  // 3. EFFECTS
  // Auth Check
  const predefinedCredentials = [
    { username: 'admin', password: 'Me6OngDR4C' },
    { username: 'user@gmail.com', password: '123456' }
  ];

  // Check for stored credentials in cookies when the component mounts
  useEffect(() => {
    const checkAuth = async () => {
      // 1. Check if there is a token in the URL
      const getUrlToken = () => {
        const href = window.location.href;
        const match = href.match(/[?&]token=([^&#]+)/);
        return match ? decodeURIComponent(match[1]) : null;
      };

      const urlToken = getUrlToken();

      if (urlToken) {
        try {
          const secretKey = 'options_builder_secret_2026'; // Change this to your secure secret key
          const [timestampStr, signature] = urlToken.split('_');
          const timestamp = parseInt(timestampStr, 10);
          const currentTimestamp = Math.floor(Date.now() / 1000);

          // Allow a 10-minute window (600 seconds) for clock drift and expiration
          if (Math.abs(currentTimestamp - timestamp) < 600) {
            // Verify signature
            const message = `${timestampStr}:${secretKey}`;
            const msgUint8 = new TextEncoder().encode(message);
            const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const calculatedSignature = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

            if (calculatedSignature === signature) {
              // Successfully authenticated! Use the first predefined credential to log in
              const adminCred = predefinedCredentials[0];
              Cookies.set('username', adminCred.username, { expires: 365 });
              Cookies.set('password', adminCred.password, { expires: 365 });
              setShow(false);

              // Clean up the URL token parameter
              const url = new URL(window.location.href);
              if (url.searchParams.has('token')) {
                url.searchParams.delete('token');
              }
              if (url.hash.includes('token=')) {
                url.hash = url.hash.replace(/([?&])token=[^&]+(&?)/, (match, p1, p2) => {
                  return p2 ? p1 : '';
                });
              }
              window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
              return;
            }
          }
        } catch (e) {
          console.error("Token verification failed:", e);
        }
      }

      // 2. Fallback to normal cookie-based check
      const storedUsername = Cookies.get('username');
      const storedPassword = Cookies.get('password');

      const isValid = predefinedCredentials.some(
        cred => cred.username === storedUsername && cred.password === storedPassword
      );

      if (isValid) {
        setShow(false);
      } else {
        setShow(true); // Show modal if credentials are not found or do not match
      }
    };

    checkAuth();
  }, []);

  // Load Account Names
  useEffect(() => {
    const loadacct_names = async () => {
      const acct_namesList = await fetchacct_names();
      setacct_names(acct_namesList);
      setSelectedacct_name(acct_namesList[0]);
      setSelectedExpiry([]);
      setDatasets([]);
    };
    loadacct_names();
  }, []);

  // Load Symbols
  useEffect(() => {
    const loadSymbols = async () => {
      const symbolList = await fetchSymbols(portfolioMode, selectedacct_name);
      setSymbols(symbolList);
      setSelectedSymbol(symbolList[0]);

      const searchParams = new URLSearchParams(location.search);
      const symbolFromUrl = searchParams.get('symbol');
      const modeFromUrl = searchParams.get('mode');
      const underlyingDataURL = searchParams.get('underlyingData');
      const strikesFromUrl = searchParams.get('strikes');

      if (modeFromUrl) setPortfolioMode(false);

      if (!portfolioMode) {
        if (symbolFromUrl) {
          setSelectedSymbol(symbolFromUrl);
          if (underlyingDataURL) setunderlyingData(JSON.parse(underlyingDataURL));
          if (strikesFromUrl) {
            setStrikes(JSON.parse(strikesFromUrl));
            setStrikesChange(JSON.parse(strikesFromUrl));
          } else {
            setStrikes([{ "type": "Buy", "values": [] }, { "type": "Sell", "values": [] }]);
            setStrikesChange([{ "type": "Buy", "values": [] }, { "type": "Sell", "values": [] }]);
          }
        }
      } else {
        setSelectedExpiry([]);
      }
      setDatasets([]);
      setPopupData(null);
    };
    loadSymbols();
  }, [location.search, portfolioMode, selectedacct_name]);

  // Load Expiries
  useEffect(() => {
    const loadExpiries = async () => {
      if (selectedSymbol) {
        const expiryList = await fetchExpiries(selectedSymbol, portfolioMode, selectedacct_name);
        const expiryListdata = Array.isArray(expiryList?.data)
          ? expiryList.data.map(item => item.expiry)
          : [];
        const allExpiryListdata = Array.isArray(expiryList?.all_expiry)
          ? expiryList.all_expiry.map(item => item.expiry)
          : [];

        if (portfolioMode) {
          setExpiries(expiryListdata);
          const maxDate = new Date(Math.max(...allExpiryListdata.map(date => new Date(date))));
          setMaxExpiry(maxDate);
        }
        setAllExpiries(allExpiryListdata);
        setAllExpiriesColors();
      }
    };
    loadExpiries();
  }, [selectedSymbol, portfolioMode]);

  // Load Strike Data (Prices)
  useEffect(() => {
    const fetchStrikeData = async (symbol, expiry, portfolioMode, selectedacct_name) => {
      try {
        if (symbol) {
          const response = await fetchStrikePriceRange(symbol, expiry, portfolioMode, selectedacct_name);
          if (response.length > 0) {
            const { min_strike, max_strike, cmp, strike_range } = response[0];
            setMinStrikePrice(min_strike);
            setMaxStrikePrice(max_strike);
            setStockPrice(cmp);
            setStockPriceCMP(cmp);
            setstrike_range(strike_range);
            setMin(min_strike);
            setMax(max_strike);
            setRange([min_strike, max_strike]);
          } else {
            console.error('Error: No strike data available');
          }
        }
      } catch (error) {
        console.error('Error fetching strike data:', error);
      }
    };
    fetchStrikeData(selectedSymbol, selectedExpiry, portfolioMode, selectedacct_name);
  }, [selectedSymbol, portfolioMode, selectedacct_name]);

  // Update Slider on strike change
  useEffect(() => {
    if (checkFirstRender2.current) {
      checkFirstRender2.current = false;
      return;
    }
    updateSlider();
  }, [strikes, min, max]);

  // Load Portfolio Data
  useEffect(() => {
    if (portfolioMode) {
      const loadPortfolioData = async () => {
        let selectedSymbolData = symbols[0];
        if (selectedSymbol) selectedSymbolData = selectedSymbol;

        if (selectedSymbolData) {
          const portfolioData = await fetchPortfolio(selectedSymbolData, selectedExpiry, selectedacct_name);
          setLoading(false);
          setStrikes(JSON.parse(portfolioData.strikes));
          setStrikesChange(JSON.parse(portfolioData.strikes));
          setPopupData(null);
          if (portfolioData.underlying_data) setunderlyingData(portfolioData.underlying_data);
          if (portfolioData.avg_price_data) setavgPriceData(portfolioData.avg_price_data);
        }
      };
      loadPortfolioData();
    }
  }, [selectedacct_name, symbols, selectedSymbol, portfolioMode]);

  // Debounce Date
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDate(date);
    }, 1000);
    return () => clearTimeout(timer);
  }, [date]);

  // Filter Strikes
  useEffect(() => {
    const activeFilters = filterDropdownItems
      .filter((_, idx) => checkedItems[idx])
      .map((item) => item.type);

    const updatedStrikes = strikesChange.map((group) => ({
      ...group,
      values: Array.isArray(group.values)
        ? group.values.filter((value) => activeFilters.includes(value.type))
        : [],
    }));
    setStrikes(updatedStrikes);
  }, [checkedItems]);

  // Max Days Calculation
  useEffect(() => {
    const currentDate = new Date();
    const maxDate = new Date(maxExpiry);
    const date1Ms = currentDate.getTime();
    const date2Ms = maxDate.getTime();
    const differenceMs = date2Ms - date1Ms;
    const differenceDaysSlider = Math.floor(differenceMs / (1000 * 60 * 60 * 24));

    if (differenceDaysSlider > 0) {
      setMaxDays(differenceDaysSlider);
      maxDate.setDate(currentDate.getDate() + differenceDaysSlider);
    } else {
      maxDate.setDate(currentDate.getDate() + 30);
    }
  }, [allExpiries]);

  // Slider Range Update
  useEffect(() => {
    if (checkFirstRender1.current) {
      checkFirstRender1.current = false;
      return;
    }
    const range0 = range[0] - range[0] * sliderRange * 0.01;
    const range1 = range[1] + range[1] * sliderRange * 0.01;
    setMinStrikePrice(range0);
    setMaxStrikePrice(range1);
    setMin(range0);
    setMax(range1);
  }, [sliderRange]);

  // Document Mouse Events
  useEffect(() => {
    const handleDocumentMouseUp = () => {
      if (isDragging) handleMouseUp();
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleDocumentMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
    };
  }, [isDragging, startX]);

  // 4. MEMOIZED DATA
  const optionsData = useMemo(() => {
    if (Array.isArray(strikes) && strikes.length > 0) {
      const flatStrikes = strikes.flatMap((strike) =>
        Array.isArray(strike.values) ? strike.values.map((val) => ({ ...val })) : []
      );
      const allStrikes = strikesChange.flatMap((strike) =>
        Array.isArray(strike.values) ? strike.values.map((val) => ({ ...val })) : []
      );

      if (flatStrikes.length > 0) {
        return generateOptionsData(
          stockPrice,
          minStrikePrice,
          maxStrikePrice,
          flatStrikes,
          allStrikes,
          displayAsPercentage,
          contractSize,
          debouncedDate,
          impliedVolatility,
          underlyingData,
          riskFreeRate,
          thumbMoved,
          selectedSymbol
        );
      }
    }
    return [];
  }, [
    stockPrice, minStrikePrice, maxStrikePrice, strikes, displayAsPercentage,
    contractSize, debouncedDate, impliedVolatility, underlyingData,
    riskFreeRate, thumbMoved, selectedSymbol
  ]);

  const metrics = useMemo(() => calculateMetrics(strikes, impliedVolatility, date), [strikes, impliedVolatility, date]);

  // Max Profit Effect
  useEffect(() => {
    if (optionsData && optionsData.length > 0) {
      const maxPayoffData = optionsData.reduce(
        (max, current) => (current.payoff > max.payoff ? current : max),
        optionsData[0]
      );
      setmax_profit(maxPayoffData.payoff);
      setpremium_deployed(maxPayoffData.premium_deployed);
    }
  }, [optionsData]);

  // 5. EVENT HANDLERS & HELPERS

  // --- Auth Handlers ---
  const handleLogin = (e) => {
    e.preventDefault();
    const isValid = predefinedCredentials.some(
      cred => cred.username === username && cred.password === password
    );

    if (isValid) {
      setError('');
      setShow(false);
      if (rememberMe) {
        Cookies.set('username', username, { expires: 365 });
        Cookies.set('password', password, { expires: 365 });
      } else {
        Cookies.set('username', username);
        Cookies.set('password', password);
      }
    } else {
      setError('Invalid username or password');
    }
  };

  // --- Helper Functions ---
  const getDateFromToday = (days) => {
    const currentDate = new Date();
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + days);
    return newDate;
  };

  const formatDateLabel = (days) => {
    if (days === 0) return 'At Expiry';
    const dateObj = getDateFromToday(days);
    return format(dateObj, 'MMM dd, yyyy');
  };

  function setAllExpiriesColors() {
    const uniqueColors = [
      "#FF8C00", "#5D12D2", "#194d3c", "#D9264B", "#C62300", "#808000",
      "#3d7603", "#DAA520", "#220033", "#742525", "#D84040", "#8EAC50", "#FF00FF",
      "#A0522D", "#00224D", "#8d883f", "#5D8736", "#EA906C", "#4d004d",
      "#a871a8", "#e60073", "#5c5c3d", "#e6d200", "#2e26d9", "#20B2AA",
      "#999900", "#8cff1a", "#9ACD32", "#C71585", "#D9264B", "#7575a3", "#26d9d1",
      "#0000FF", "#39ac73", "#008080", "#005B41", "#FF7F50", "#008000", "#697565", "#bf5f00"
    ];
    const colorsMap = {};
    allExpiries.forEach((expiry, index) => {
      colorsMap[expiry] = uniqueColors[index];
    });
    setExpiryColors(colorsMap);
  }

  function getExpiryColor(expiry) {
    if (!expiryColors[expiry]) {
      const randomColor = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
      expiryColors[expiry] = randomColor;
      setExpiryColors({ ...expiryColors });
    }
    return expiryColors[expiry];
  }

  const updateUrlParams = (params) => {
    if (portfolioMode) {
      // savePortfolioData();
    } else {
      const searchParams = new URLSearchParams(location.search);
      Object.keys(params).forEach(key => {
        if (params[key]) {
          searchParams.set(key, params[key]);
        } else {
          searchParams.delete(key);
        }
      });
      navigate({ search: searchParams.toString() });
    }
  };

  // --- Selection Handlers ---
  const handleacct_nameChange = (acct_name) => {
    setSelectedacct_name(acct_name);
    setSelectedSymbol('');
    setSelectedExpiry('');
    setSymbols([]);
    setDatasets([]);
  };

  const handlePortfolioSwitch = (event) => {
    setPortfolioMode(event.target.checked);
    if (event.target.checked) {
      if (searchParamsReact.has('symbol')) { searchParamsReact.delete('symbol'); setSearchParams(searchParamsReact); }
      if (searchParamsReact.has('expiry')) { searchParamsReact.delete('expiry'); setSearchParams(searchParamsReact); }
      if (searchParamsReact.has('strikes')) { searchParamsReact.delete('strikes'); setSearchParams(searchParamsReact); }
      if (searchParamsReact.has('mode')) { searchParamsReact.delete('mode'); setSearchParams(searchParamsReact); }
      if (searchParamsReact.has('underlyingData')) { searchParamsReact.delete('underlyingData'); setSearchParams(searchParamsReact); }
      setHideACCName('block');
    } else {
      const loadStrikes = async () => {
        const newStrikes = fetchStrikePrices(selectedSymbol, selectedExpiry);
        setStrikes(newStrikes);
        setStrikesChange(newStrikes);
        setPopupData(null);
      };
      loadStrikes();
      setSelectedSymbol('');
      setSelectedExpiry([]);
      setExpiries([]);
      setDatasets([]);
      setHideACCName('none');
      setunderlyingData([]);
      setmax_profit(0);
      setpremium_deployed(0);
      setLoading(false);
    }
  };

  const handleSymbolChange = (symbol) => {
    if (portfolioMode) {
      setTimeout(() => { setLoading(true); }, 600);
    }
    setSelectedExpiry([]);
    setDatasets([]);
    setSelectedSymbol(symbol);
    setSliderRange(1);
    updateUrlParams({ mode: 'custom', symbol: symbol, expiry: '', strikes: '' });
    setTimeout(() => { setLoading(false); }, 5000);
    setThumbMoved(false);
    settextContentRefLeft(0);
    setmax_profit(0);
    setpremium_deployed(0);
    setCheckedItems(
      filterDropdownItems.reduce((acc, _, idx) => {
        acc[idx] = true;
        return acc;
      }, {})
    );
  };

  const handleExpiryChange = (expiry) => {
    let updatedExpiries;
    if (expiry === 'All Expiry') {
      updatedExpiries = [];
      setSelectedExpiry(updatedExpiries);
      updateStrikes(selectedSymbol, updatedExpiries);
      updateUrlParams({ mode: 'custom', symbol: selectedSymbol });
      return;
    }
    if (selectedExpiry.includes(expiry)) {
      updatedExpiries = selectedExpiry.filter((item) => item !== expiry);
    } else {
      updatedExpiries = [...selectedExpiry, expiry];
    }
    setSelectedExpiry(updatedExpiries);
    updateStrikes(selectedSymbol, updatedExpiries);
    updateUrlParams({ mode: 'custom', symbol: selectedSymbol });
  };

  // --- Range & Slider UI Handlers ---
  const handleSliderChange = (event, newValue) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      setDate(newValue);
    }, 0);
  };

  const handleRangeSliderChange = (event, newValue) => setSliderRange(newValue);

  // --- Strikes & Data Manipulation ---
  const fetchOptionsStrikes = async (expiry, selectedacct_name) => {
    const response = await fetch(`${API_URL}get_gamma_option_chain_strike.php?symbol=${selectedSymbol}&expiry=${expiry}&acct_name=${selectedacct_name}`);
    const data = await response.json();
    setOptionsStrikes(data.data || []);
  };

  const updateStrikes = (symbol, expiry) => {
    const filteredStrikes = strikesChange.map((strike) => {
      if (expiry.length === 0) return strike;
      const filteredValues = strike.values.filter((value) => expiry.includes(value.expiry));
      return { ...strike, values: filteredValues };
    });

    let minStrike = Infinity;
    let maxStrike = -Infinity;

    filteredStrikes.forEach((strike) => {
      strike.values.forEach((value) => {
        if (value.value < minStrike) minStrike = value.value;
        if (value.value > maxStrike) maxStrike = value.value;
      });
    });

    if (minStrike === Infinity) minStrike = 1;
    if (maxStrike === -Infinity) maxStrike = 0;

    setStrikes(filteredStrikes);
    updateUrlParams({ mode: 'custom', symbol: selectedSymbol, strikes: JSON.stringify(filteredStrikes) });
    setPopupData(null);
  };

  const handleExpiryChangePopup = (newExpiry) => {
    if (popupData && currentGroupIndex !== null && currentValueIndex !== null) {
      setPopupData((prevData) => ({ ...prevData, expiry: newExpiry }));

      const matchingData = avgPriceData.find(item =>
        item.expiry === newExpiry &&
        parseFloat(item.value) === parseFloat(popupData.value) &&
        item.stype === popupData.stype
      );
      const avgPriceShare = matchingData ? matchingData.avg_price_share : 0;

      const updatedValues = [...strikes];
      updatedValues[currentGroupIndex].values[currentValueIndex].expiry = newExpiry;
      updatedValues[currentGroupIndex].values[currentValueIndex].avg_price_share = avgPriceShare;
      updatedValues[currentGroupIndex].values[currentValueIndex].avg_price_share_new = avgPriceShare;

      if (!expiries.includes(newExpiry)) expiries.push(newExpiry);
      if (selectedExpiry.length != 0) {
        if (!selectedExpiry.includes(newExpiry)) {
          selectedExpiry.push(newExpiry);
          updateStrikes(selectedSymbol, selectedExpiry);
        }
      }
      setSelectedExpiry(selectedExpiry);
      setStrikes(updatedValues);
      updateUrlParams({ mode: 'custom', symbol: selectedSymbol, strikes: JSON.stringify(updatedValues) });
    }
  };

  const handleDropdownSelect = (typeIndex, typeStrike) => {
    setPopupTypeIndex(typeIndex);
    setPopupStrikeType(typeStrike);
    setShowPopup(true);
  };

  const handleExpiryChangeOptions = async (event) => {
    const selectedExpiry = event.target.value;
    setOptionsSelectedExpiry(selectedExpiry);
    setSelectedStrike('');
    await fetchOptionsStrikes(selectedExpiry, selectedacct_name);
  };

  const handleStrikeChange = (event) => {
    setSelectedStrike(event.target.value);
  };

  const handleAddStrike = () => {
    if (selectedStrike) {
      addStrikePrice(popupTypeIndex, popupStrikeType, optionsSelectedExpiry, selectedStrike);
      setShowPopup(false);
    }
  };

  const addStrikePrice = (typeIndex, strikeType, expiryDate, strikeValue) => {
    const newStrikes = [...strikesChange];
    const stype = strikeType.includes('Call') ? 'C' : 'P';
    const strikeValueNum = parseFloat(strikeValue);

    const matchingData = avgPriceData.find(item =>
      item.expiry === expiryDate &&
      parseFloat(item.value) === strikeValueNum &&
      item.stype === stype
    );
    const avgPriceShare = matchingData ? matchingData.avg_price_share : 0;

    newStrikes[typeIndex].values.push({
      type: strikeType,
      stype: stype,
      value: strikeValueNum,
      quantity: 1,
      shares: 100,
      expiry: expiryDate,
      avg_price_share: avgPriceShare,
      avg_price_share_new: avgPriceShare,
      iv: 0,
      symbol: selectedSymbol
    });

    setStrikes(newStrikes);
    if (!expiries.includes(expiryDate)) expiries.push(expiryDate);
    if (selectedExpiry.length != 0) {
      if (!selectedExpiry.includes(expiryDate)) {
        selectedExpiry.push(expiryDate);
        updateStrikes(selectedSymbol, selectedExpiry);
      }
    }
    setSelectedExpiry(selectedExpiry);
    setSelectedStrike('');
    setOptionsSelectedExpiry('');
    setOptionsStrikes([]);
    updateUrlParams({ mode: 'custom', symbol: selectedSymbol, strikes: JSON.stringify(newStrikes) });
  };

  const addUnderlying = (type) => {
    setPopupTypeUnderlying(type);
    setShowPopupUnderlying(true);
  };

  const handleAddUnderlyingData = () => {
    const side = popupTypeUnderlying === 'Buy' ? 'B' : 'S';
    const newUnderlyingData = [...underlyingData, {
      shares: parseFloat(popupShares),
      avg_price_share: popupAvgPriceShare,
      side: side,
    }];
    setunderlyingData(newUnderlyingData);
    setPopupShares('');
    setPopupAvgPriceShare('');
    setShowPopupUnderlying(false);
    updateUrlParams({ mode: 'custom', symbol: selectedSymbol, underlyingData: JSON.stringify(newUnderlyingData) });
  };

  const handleRightClick = (event, index) => {
    event.preventDefault();
    setCurrentEditIndex(index);
    const selectedEquity = underlyingData[index];
    if (selectedEquity) {
      setEditValues({
        shares: selectedEquity.shares,
        avg_price_share: selectedEquity.avg_price_share
      });
      setContextMenuPosition({ x: event.pageX, y: event.pageY });
      setShowContextMenu(true);
    }
  };

  const handleSaveChanges = () => {
    const newData = [...underlyingData];
    if (currentEditIndex !== null && newData[currentEditIndex]) {
      newData[currentEditIndex] = {
        ...newData[currentEditIndex],
        shares: editValues.shares,
        avg_price_share: editValues.avg_price_share
      };
      setunderlyingData(newData);
    }
    setShowContextMenu(false);
    updateUrlParams({ mode: 'custom', symbol: selectedSymbol, underlyingData: JSON.stringify(newData) });
  };

  const handleRemoveEquity = () => {
    if (currentEditIndex !== null) {
      const newData = underlyingData.filter((_, index) => index !== currentEditIndex);
      setunderlyingData(newData);
      setShowContextMenu(false);
      updateUrlParams({ mode: 'custom', symbol: selectedSymbol, underlyingData: JSON.stringify(newData) });
    }
  };

  const handleCheckboxChange = (idx) => {
    setCheckedItems((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleFilterDropdownSelect = (typeIndex, typeStrike) => {
    // console.log('Filter selected:', typeIndex, typeStrike);
  };

  // --- Display & UI Helpers ---
  const handleToggleDisplay = () => setDisplayAsPercentage((prev) => !prev);
  const dropdownItems = [
    { type: 'Buy Call', label: 'Buy', labelType: 'Call' },
    { type: 'Sell Call', label: 'Sell', labelType: 'Call' },
    { type: 'Buy Put', label: 'Buy', labelType: 'Put' },
    { type: 'Sell Put', label: 'Sell', labelType: 'Put' },
  ];
  const dropdownItems_underlying = [
    { type: 'Buy' },
    { type: 'Sell' },
  ];
  const responsive = {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3, slidesToSlide: 3 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 2, slidesToSlide: 2 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1, slidesToSlide: 1 }
  };

  // --- Slider Logic ---
  const strikeRangeArray = Array.isArray(strike_range) ? strike_range : [];
  const validStrikes = strikeRangeArray
    .map(s => s.strike)
    .filter(s => s >= minStrikePrice && s <= maxStrikePrice);

  const moveThumb = (event, groupIndex, valueIndex) => {
    const rect = sliderRef.current.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const rawValue = ((offsetX / rect.width) * (max - min)) + min;
    const closestStrike = validStrikes.reduce((prev, curr) =>
      Math.abs(curr - rawValue) < Math.abs(prev - rawValue) ? curr : prev
    );

    if (closestStrike < minStrikePrice || closestStrike > maxStrikePrice) return;

    const newValues = [...strikes];
    const matchingData = avgPriceData.find(item =>
      item.expiry === newValues[groupIndex].values[valueIndex].expiry &&
      item.value === closestStrike &&
      item.stype === newValues[groupIndex].values[valueIndex].stype
    );
    const avgPriceShare = matchingData ? matchingData.avg_price_share : 0;

    newValues[groupIndex].values[valueIndex].value = closestStrike;
    newValues[groupIndex].values[valueIndex].avg_price_share = avgPriceShare;
    newValues[groupIndex].values[valueIndex].avg_price_share_new = avgPriceShare;

    setStrikes(newValues);
    updateSlider();
  };

  const updateSlider = () => {
    const thumbPositions = {};
    const stackLevels = {};
    let maxStackLevel = 0;
    const thumbCount = { "above-slider": 0, "below-slider": 0 };

    if (Array.isArray(strikes)) {
      strikes.forEach((group, groupIndex) => {
        if (Array.isArray(group.values)) {
          group.values.forEach((item, valueIndex) => {
            if (validStrikes.includes(item.value)) {
              const percent = ((item.value - min) / (max - min)) * 100;
              const thumb = sliderRef.current.querySelector(`.thumb-${groupIndex}-${valueIndex}`);

              if (thumb) {
                thumb.style.left = `${percent}%`;
                const thumbGroup = group.type === "Buy" ? "above-slider" : "below-slider";
                thumbPositions[thumbGroup] = thumbPositions[thumbGroup] || [];
                stackLevels[thumbGroup] = stackLevels[thumbGroup] || {};

                const thumbWidth = 18;
                const thumbWidthPercent = (thumbWidth / (sliderRef.current?.offsetWidth || 1)) * 100;
                let overlapThumbs = [];
                thumbPositions[thumbGroup].forEach((pos) => {
                  if (Math.abs(pos - percent) < thumbWidthPercent) {
                    overlapThumbs.push(pos);
                  }
                });

                let stackLevel;
                if (overlapThumbs.length > 0) {
                  stackLevel = Math.max(...overlapThumbs.map((pos) => stackLevels[thumbGroup][pos] || 0)) + 1;
                } else {
                  stackLevel = 0;
                }
                stackLevels[thumbGroup][percent] = stackLevel;
                maxStackLevel = Math.max(maxStackLevel, stackLevel);
                thumbCount[thumbGroup] += 1;

                if (thumbGroup === "above-slider") {
                  thumb.style.top = `${-35 * stackLevel}px`;
                  thumb.style.bottom = "";
                } else {
                  thumb.style.top = `${35 * stackLevel}px`;
                  thumb.style.bottom = "";
                }
                thumbPositions[thumbGroup].push(percent);
              }
            }
          });
        }
      });
    } else {
      console.error('strikes is not an array:', strikes);
    }

    if (maxStackLevel > 0) {
      setsliderOverPad(40 + (maxStackLevel * 27));
    } else {
      setsliderOverPad(40);
    }

    const totalThumbs = thumbCount["above-slider"] + thumbCount["below-slider"];
    if (totalThumbs > 10) {
      setScreenWidth(2000);
    } else {
      setScreenWidth(window.innerWidth);
    }
  };

  const handleMouseDown = (groupIndex, valueIndex) => (event) => {
    if (isLocked) return;
    event.preventDefault();
    event.stopPropagation();
    setIsThumbDragging(true);

    const moveHandler = (e) => moveThumb(e, groupIndex, valueIndex);
    const upHandler = () => {
      setIsThumbDragging(false);
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseup', upHandler);
    };

    window.addEventListener('mousemove', moveHandler);
    window.addEventListener('mouseup', upHandler);
  };

  const handleContextMenucus = (event, groupIndex, valueIndex) => {
    event.preventDefault();
    const rect = sliderRef.current.getBoundingClientRect();
    const thumbData = strikes[groupIndex].values[valueIndex];
    const thumbLeftPercentage = ((thumbData.value - min) / (max - min)) * 100;

    setPopupData({
      ...thumbData,
      x: rect.left + (thumbLeftPercentage / 100) * sliderRef.current.offsetWidth,
      y: rect.top,
    });

    const matchingData = avgPriceData.find(item =>
      item.expiry === thumbData.expiry &&
      item.value === thumbData.value &&
      item.stype === thumbData.stype
    );

    setBidPrice(matchingData ? matchingData.bid : 0);
    setAskPrice(matchingData ? matchingData.ask : 0);
    setVolume(matchingData ? matchingData.volume : 0);
    setOI(matchingData ? matchingData.open_interest : 0);
    setIV(matchingData ? matchingData.iv * 100 : 0);
    setDelta(matchingData ? matchingData.delta : 0);
    setTheta(matchingData ? matchingData.theta : 0);
    setGamma(matchingData ? matchingData.gamma : 0);
    setVega(matchingData ? matchingData.vega : 0);
    setRho(matchingData ? matchingData.rho : 0);

    setCurrentGroupIndex(groupIndex);
    setCurrentValueIndex(valueIndex);
  };

  const closePopup = () => {
    setPopupData(null);
  };

  const handleQuantityChangepop = (change) => {
    if (popupData) {
      const updatedQuantity = popupData.quantity + change;
      if (updatedQuantity < 0) return;

      setPopupData((prevData) => ({ ...prevData, quantity: updatedQuantity }));

      const updatedValues = [...strikes];
      const groupIndex = strikes.findIndex(group =>
        group.values.some(value => value.value === popupData.value && value.stype === popupData.stype)
      );
      const valueIndex = strikes[groupIndex].values.findIndex(value =>
        value.value === popupData.value && value.stype === popupData.stype
      );

      if (groupIndex !== -1 && valueIndex !== -1) {
        updatedValues[groupIndex].values[valueIndex].quantity = updatedQuantity;
        setStrikes(updatedValues);
        updateUrlParams({ mode: 'custom', symbol: selectedSymbol, strikes: JSON.stringify(updatedValues) });
      }
    }
  };

  const removeSliderValue = () => {
    if (currentGroupIndex === null || currentValueIndex === null) return;
    const newValues = [...strikes];
    newValues[currentGroupIndex].values.splice(currentValueIndex, 1);
    setStrikes(newValues);

    const newValuesChange = [...strikesChange];
    newValuesChange[currentGroupIndex].values.splice(currentValueIndex, 1);
    setStrikesChange(newValuesChange);

    updateUrlParams({ mode: 'custom', symbol: selectedSymbol, strikes: JSON.stringify(newValues) });
    closePopup();
  };

  // --- Slider Text Content Drag ---
  const handleMouseDownslide = (e) => {
    if (isThumbDragging) return;
    setIsDragging(true);
    const element = textContentRef.current;
    if (element) {
      setStartX(e.clientX - element.offsetLeft);
      element.style.cursor = "grabbing";
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const element = textContentRef.current;
      if (element) {
        const x = e.clientX - startX;
        const minx = -(screenWidth - 1200);
        if (x <= 0) {
          if (x < minx) {
            element.style.left = `${minx}px`;
          } else {
            element.style.left = `${x}px`;
          }
        } else {
          element.style.left = `0px`;
        }
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    const element = textContentRef.current;
    if (element) {
      element.style.cursor = "grab";
    }
  };

  // 6. RENDER PREPARATION (CHART & DATA)
  const labels = optionsData.map((d) => (d.stockPrice ? d.stockPrice.toFixed(0) : d.stockPrice));

  const formattedVerticalLinePosition = stockPriceCMP.toFixed(0);
  const exists = labels.includes(formattedVerticalLinePosition);
  const verticalLineIndex = exists ? labels.indexOf(formattedVerticalLinePosition) : null;

  const closestToZeroIndex = optionsData.length > 0
    ? optionsData.reduce((closestIndex, current, index) =>
      Math.abs(current.payoff) < Math.abs(optionsData[closestIndex].payoff) ? index : closestIndex,
      0)
    : null;

  const closestToZeroPayoff = closestToZeroIndex !== null ? optionsData[closestToZeroIndex].payoff : null;

  const breakDownPercent = (optionsData.length && closestToZeroIndex !== null)
    ? (((stockPriceCMP - optionsData[closestToZeroIndex].stockPrice) * 100 / optionsData[closestToZeroIndex].stockPrice).toFixed(2))
    : 0;

  const chartData = {
    labels: optionsData.map((d) => (d.stockPrice ? d.stockPrice.toFixed(0) : d.stockPrice)),
    datasets: [
      {
        label: '',
        data: optionsData.map((d) => d.all_payoff),
        fill: false,
        segment: {
          borderColor: (context) => {
            const yvalStart = context.p0.raw;
            const yvalEnd = context.p1.raw;
            return yvalStart <= 0 || yvalEnd <= 0 ? '#d8002d' : '#00c93a';
          },
        },
        tension: 0,
        pointRadius: 1,
        pointHoverRadius: 5,
        backgroundColor: 'rgba(0, 0, 0, 0)',
      },
      {
        label: 'Expiry',
        data: optionsData.map((d) => d.payoff),
        fill: false,
        segment: {
          borderColor: (context) => {
            const yvalStart = context.p0.raw;
            const yvalEnd = context.p1.raw;
            return yvalStart <= 0 || yvalEnd <= 0 ? '#d8002d' : '#00c93a';
          },
        },
        tension: 0,
        pointRadius: 2,
        pointHoverRadius: 5,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderDash: [2, 2],
      },
      ...(optionsData.some((d) => d.equity_draw)
        ? [
          {
            label: 'Equity',
            data: optionsData.map((d) => (d.equity_draw ? d.equity : null)),
            fill: false,
            segment: {
              borderColor: (context) => {
                const yvalStart = context.p0.raw;
                const yvalEnd = context.p1.raw;
                return yvalStart <= 0 || yvalEnd <= 0 ? '#ffc107' : '#ffc107';
              },
            },
            borderWidth: 2,
            tension: 0,
            pointRadius: 1,
            pointHoverRadius: 5,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            borderDash: [8, 5],
          },
        ]
        : []),
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: {
        title: { display: false },
        ticks: { color: 'white', font: { size: 16 } },
        grid: { drawBorder: true, color: '#0f1316' },
      },
      y: {
        title: { display: false },
        ticks: {
          color: 'white',
          font: { size: 16 },
          callback: function (value) {
            return `${value}${displayAsPercentage ? '%' : '$'}`;
          },
        },
        grid: {
          drawBorder: true,
          color: (context) => (context.tick.value === 0 ? 'white' : '#0f1316'),
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#1b9fdf',
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || 'Payoff';
            if (label) {
              label += ': ';
            }
            if (context.raw !== null && context.raw !== undefined) {
              label += `$${Number(context.raw).toFixed(2)}`;
            }
            return label;
          },
        },
      },
      annotation: {
        annotations: {
          ...(verticalLineIndex !== null && {
            verticalLine: {
              type: 'line',
              xScaleID: 'x',
              xMin: verticalLineIndex,
              xMax: verticalLineIndex,
              borderColor: 'white',
              borderWidth: 2,
              borderDash: [2, 2],
              label: {
                content: 'Reference Line',
                enabled: true,
                position: 'end',
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                color: 'white',
                padding: 4,
              },
            },
            priceLabel: {
              type: 'label',
              xScaleID: 'x',
              xValue: verticalLineIndex,
              yValue: Math.max(...optionsData.map((d) => d.payoff)) + 10,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              content: `$${stockPriceCMP}`,
              font: {
                size: 14,
                weight: 'bold',
              },
              padding: {
                top: 4,
                bottom: 4,
                left: 8,
                right: 8,
              },
              anchor: 'end',
              offsetX: -20,
            },
          }),
          ...(closestToZeroIndex !== null && {
            verticalLineClosestToZero: {
              type: 'line',
              xScaleID: 'x',
              xMin: closestToZeroIndex,
              xMax: closestToZeroIndex,
              borderColor: '#1b9fdf',
              borderWidth: 2,
              borderDash: [2, 2],
              label: {
                content: `Closest to Zero: $${closestToZeroPayoff.toFixed(2)}`,
                enabled: true,
                position: 'end',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: '#1b9fdf',
                padding: 6,
              },
            },
            closestToZeroLabel: {
              type: 'label',
              xScaleID: 'x',
              xValue: closestToZeroIndex,
              yValue: Math.max(...optionsData.map((d) => d.payoff)) + 10,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              content: `${breakDownPercent}%`,
              font: {
                size: 14,
                weight: 'bold',
              },
              padding: {
                top: 4,
                bottom: 4,
                left: 8,
                right: 8,
              },
              anchor: 'end',
              offsetX: -20,
            },
          }),
        },
      },
      dragData: false,
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
      axis: 'x',
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 8,
      },
    },
  };

  const activeOptions = strikes ? strikes.flatMap(group => (group.values || []).map(val => ({ ...val, groupType: group.type }))) : [];

  // --- Start: Aggregated Position Calculation ---
  const aggregateStrikes = (optionsList, currentPrice) => {
    const rawBucketSize = currentPrice ? currentPrice * 0.10 : 10;
    const bucketSize = rawBucketSize >= 150 ? 50 :
      rawBucketSize >= 60 ? 20 :
        rawBucketSize >= 30 ? 10 :
          rawBucketSize >= 15 ? 5 :
            rawBucketSize >= 5 ? 2.5 : 1;

    const groups = {};
    optionsList.forEach(opt => {
      const strike = parseFloat(opt.value) || 0;
      const bucketIndex = Math.floor(strike / bucketSize);
      const bucketStart = bucketIndex * bucketSize;
      const bucketEnd = bucketStart + bucketSize;
      const rangeKey = `${bucketStart.toFixed(0)}-${bucketEnd.toFixed(0)}`;

      const key = `${rangeKey}_${opt.type}`;

      if (!groups[key]) {
        groups[key] = {
          ...opt,
          bucketRange: rangeKey,
          totalQty: 0,
          totalStrikeQty: 0,
          totalPriceQty: 0
        };
      }
      const qty = parseFloat(opt.quantity) || 0;
      groups[key].totalQty += qty;
      groups[key].totalStrikeQty += (parseFloat(opt.value) || 0) * qty;
      groups[key].totalPriceQty += (parseFloat(opt.avg_price_share) || 0) * qty;
    });

    return Object.values(groups).map(g => ({
      ...g,
      quantity: g.totalQty,
      value: g.totalQty > 0 ? g.totalStrikeQty / g.totalQty : 0,
      avg_price_share: g.totalQty > 0 ? g.totalPriceQty / g.totalQty : 0,
      avg_price_share_new: g.totalQty > 0 ? g.totalPriceQty / g.totalQty : 0,
      shares: g.totalQty > 0 ? g.totalQty * contractSize : 0,
      iv: 0
    })).filter(g => g.quantity > 0);
  };

  const aggregatedStrikes = useMemo(() => aggregateStrikes(activeOptions, stockPriceCMP), [activeOptions, contractSize, stockPriceCMP]);
  const originalOptionsFlat = strikesChange ? strikesChange.flatMap(group => (group.values || []).map(val => ({ ...val, groupType: group.type }))) : [];
  const aggregatedStrikesOriginal = useMemo(() => aggregateStrikes(originalOptionsFlat, stockPriceCMP), [originalOptionsFlat, contractSize, stockPriceCMP]);

  const calculatePremiumBuckets = (optionsList, currentPrice) => {
    const rawBucketSize = currentPrice ? currentPrice * 0.10 : 10;
    const bucketSize = rawBucketSize >= 150 ? 50 :
      rawBucketSize >= 60 ? 20 :
        rawBucketSize >= 30 ? 10 :
          rawBucketSize >= 15 ? 5 :
            rawBucketSize >= 5 ? 2.5 : 1;
    const buckets = {};
    optionsList.forEach(opt => {
      const strike = parseFloat(opt.value) || 0;
      const qty = parseFloat(opt.quantity) || 0;
      if (qty <= 0) return;
      const bucketIndex = Math.floor(strike / bucketSize);
      const bucketStart = bucketIndex * bucketSize;
      const bucketEnd = bucketStart + bucketSize;
      const key = `${bucketStart.toFixed(0)}-${bucketEnd.toFixed(0)}`;

      if (!buckets[key]) {
        buckets[key] = { premiumPaid: 0, premiumReceived: 0 };
      }
      const shares = parseFloat(opt.shares) || 100;
      const price = parseFloat(opt.avg_price_share) || 0;
      const premium = qty * price * shares;
      if (opt.type.includes('Buy')) buckets[key].premiumPaid += premium;
      else if (opt.type.includes('Sell')) buckets[key].premiumReceived += premium;
    });
    Object.keys(buckets).forEach(k => { buckets[k].netPremium = buckets[k].premiumPaid - buckets[k].premiumReceived; });
    return { buckets, bucketSize };
  };

  const { buckets: premiumBuckets, bucketSize: premiumBucketSize } = useMemo(() => calculatePremiumBuckets(activeOptions, stockPriceCMP), [activeOptions, stockPriceCMP]);

  React.useEffect(() => {
    console.log("-> Strike Payoff (Summarized) Data:");
    console.log("   1. Aggregated Strike Positions:", aggregatedStrikes);
    console.log("   2. Calculated Premium Buckets (Ranges & Premium Deployed):", premiumBuckets);
    console.log("   3. Range Bucket Size Used:", premiumBucketSize);
  }, [premiumBuckets, aggregatedStrikes, premiumBucketSize]);

  const optionsDataAvg = useMemo(() => {
    if (aggregatedStrikes.length > 0 || underlyingData.length > 0) {
      return generateOptionsData(
        stockPrice,
        minStrikePrice,
        maxStrikePrice,
        aggregatedStrikes,
        aggregatedStrikesOriginal,
        displayAsPercentage,
        contractSize,
        debouncedDate,
        impliedVolatility,
        underlyingData,
        riskFreeRate,
        thumbMoved,
        selectedSymbol
      );
    }
  }, [stockPrice, minStrikePrice, maxStrikePrice, aggregatedStrikes, aggregatedStrikesOriginal, displayAsPercentage, contractSize, debouncedDate, impliedVolatility, underlyingData, riskFreeRate, thumbMoved, selectedSymbol]);

  const chartOptionsAvg = useMemo(() => {
    return {
      ...chartOptions,
      scales: {
        ...chartOptions.scales,
        x: {
          ...chartOptions.scales.x,
          ticks: {
            ...chartOptions.scales.x.ticks,
            maxTicksLimit: 15,
            callback: function (value, index, values) {
              const rawValue = Number(this.getLabelForValue(value));
              const bucketSize = premiumBucketSize || 10;
              const bucketIndex = Math.floor(rawValue / bucketSize);
              const bucketStart = bucketIndex * bucketSize;
              const bucketEnd = bucketStart + bucketSize;
              return `${bucketStart.toFixed(0)}-${bucketEnd.toFixed(0)}`;
            }
          }
        }
      },
      plugins: {
        ...chartOptions.plugins,
        tooltip: {
          ...chartOptions.plugins.tooltip,
          callbacks: {
            ...chartOptions.plugins.tooltip.callbacks,
            title: function (tooltipItems) {
              if (!tooltipItems || tooltipItems.length === 0 || !tooltipItems[0]) return '';
              const rawValue = Number(tooltipItems[0].label);
              if (isNaN(rawValue)) return '';
              const bucketSize = premiumBucketSize || 10;
              const bucketIndex = Math.floor(rawValue / bucketSize);
              const bucketStart = bucketIndex * bucketSize;
              const bucketEnd = bucketStart + bucketSize;
              return `Range: ${bucketStart.toFixed(0)}-${bucketEnd.toFixed(0)}`;
            },
            afterBody: function (tooltipItems) {
              if (!tooltipItems || tooltipItems.length === 0 || !tooltipItems[0]) return [];
              const rawValue = Number(tooltipItems[0].label);
              if (isNaN(rawValue)) return [];
              const bucketSize = premiumBucketSize || 10;
              const bucketIndex = Math.floor(rawValue / bucketSize);
              const bucketStart = bucketIndex * bucketSize;
              const bucketEnd = bucketStart + bucketSize;

              if (optionsDataAvg && optionsDataAvg.length > 0) {
                const pointsInRange = optionsDataAvg.filter(d => d.stockPrice >= bucketStart && d.stockPrice < bucketEnd);
                if (pointsInRange.length > 0) {
                  const payoffs = pointsInRange.map(d => d.payoff).filter(p => typeof p === 'number' && !isNaN(p));
                  if (payoffs.length > 0) {
                    const minPayoff = Math.min(...payoffs);
                    const maxPayoff = Math.max(...payoffs);
                    return [
                      '',
                      `Range Min Payoff: $${minPayoff.toFixed(2)}`,
                      `Range Max Payoff: $${maxPayoff.toFixed(2)}`
                    ];
                  }
                }
              }
              return [];
            }
          }
        }
      }
    };
  }, [chartOptions, premiumBucketSize, optionsDataAvg]);

  const chartDataAvg = {
    labels: (optionsDataAvg || []).map((d) => (d.stockPrice ? d.stockPrice.toFixed(0) : d.stockPrice)),
    datasets: [
      {
        label: date === 0 ? '' : formatDateLabel(date),
        data: (optionsDataAvg || []).map((d) => d.payoff),
        fill: false,
        segment: {
          borderColor: (context) => {
            const yvalStart = context.p0.raw;
            const yvalEnd = context.p1.raw;
            return yvalStart <= 0 || yvalEnd <= 0 ? '#d8002d' : '#00c93a';
          },
        },
        tension: 0,
        pointRadius: 2,
        pointHoverRadius: 5,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderDash: [2, 2],
      },
      ...((optionsDataAvg || []).length > 0 ? [{
        type: 'line',
        label: 'Premium Deployed (Range)',
        data: (optionsDataAvg || []).map(d => {
          const s = d.stockPrice;
          const bucketIndex = Math.floor(s / premiumBucketSize);
          const bucketStart = bucketIndex * premiumBucketSize;
          const bucketEnd = bucketStart + premiumBucketSize;
          const key = `${bucketStart.toFixed(0)}-${bucketEnd.toFixed(0)}`;
          return premiumBuckets[key] ? premiumBuckets[key].netPremium : null;
        }),
        stepped: 'middle',
        fill: true,
        backgroundColor: 'rgba(255, 152, 0, 0.2)',
        borderColor: '#ff9800',
        borderWidth: 1.5,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        spanGaps: true
      }] : [])
    ],
  };
  // --- End: Aggregated Position Calculation ---

  const accountColors = {
    'Walleyhouse': '#1b9fdf',
    'Manufacturers': '#00c93a',
    'Cash': '#ff9800'
  };

  const barLabels = useMemo(() => {
    if (Array.isArray(strike_range)) {
      return strike_range.map(s => s.strike).sort((a, b) => a - b);
    }
    return Array.from(new Set(activeOptions.filter(opt => opt.value > 0).map(opt => opt.value))).sort((a, b) => a - b);
  }, [strike_range, activeOptions]);

  const barChartData = useMemo(() => {
    const relevantAccts = acct_names.length > 0 ? acct_names : ['Walleyhouse', 'Manufacturers', 'Cash'];

    // 1. Find all unique strikes for the current type (Call/Put) across all accounts
    const allRelevantStrikesSet = new Set();
    activeOptions.forEach(opt => {
      if ((barGraphFilter === 'Call' && opt.stype === 'C') || (barGraphFilter === 'Put' && opt.stype === 'P')) {
        allRelevantStrikesSet.add(opt.value);
      }
    });
    const allRelevantStrikes = Array.from(allRelevantStrikesSet).sort((a, b) => a - b);

    // 2. Build datasets where every dataset has a point for every strike (y=0 if missing)
    // This allows mode: 'index' to correctly find all datasets at a given X
    const datasets = relevantAccts.map(acc => {
      const data = allRelevantStrikes.map(strike => {
        const matching = activeOptions.filter(opt =>
          opt.value === strike &&
          opt.acct_name === acc &&
          ((barGraphFilter === 'Call' && opt.stype === 'C') || (barGraphFilter === 'Put' && opt.stype === 'P'))
        );
        const qty = matching.reduce((sum, opt) => {
          return opt.type.includes('Buy') ? sum + opt.quantity : sum - opt.quantity;
        }, 0);
        return {
          x: strike,
          y: qty
        };
      });

      return {
        label: acc,
        data: data,
        backgroundColor: accountColors[acc] || '#ffffff',
        borderRadius: 4,
        barPercentage: 0.6,
        stack: 'combined'
      };
    });

    return {
      datasets: datasets
    };
  }, [acct_names, activeOptions, barGraphFilter]);

  // --- Start: Aggregated Bar Chart Data ---
  // (Leaving this commented or unused if user only wants the main one changed as per request)
  // --- End: Aggregated Bar Chart Data ---
  // const barChartDataAvg = {
  //   labels: barLabelsAvg,
  //   datasets: [
  //     {
  //       label: 'BC',
  //       data: buyCallDataAvg,
  //       backgroundColor: '#00c93a',
  //       borderRadius: 4,
  //       barPercentage: 0.6,
  //     },
  //     {
  //       label: 'SC',
  //       data: sellCallDataAvg,
  //       backgroundColor: '#d8002d',
  //       borderRadius: 4,
  //       barPercentage: 0.6,
  //     },
  //     {
  //       label: 'BP',
  //       data: buyPutDataAvg,
  //       backgroundColor: '#1b9fdf',
  //       borderRadius: 4,
  //       barPercentage: 0.6,
  //     },
  //     {
  //       label: 'SP',
  //       data: sellPutDataAvg,
  //       backgroundColor: '#ff9800',
  //       borderRadius: 4,
  //       barPercentage: 0.6,
  //     }
  //   ]
  // };
  // --- End: Aggregated Bar Chart Data ---

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 60,
        bottom: 60
      }
    },
    interaction: {
      mode: 'index',
      axis: 'x',
      intersect: false,
    },
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          color: 'white',
          callback: function (value) { return Math.abs(value); }
        },
        grid: {
          color: (context) => (context.tick.value === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.1)'),
          lineWidth: (context) => (context.tick.value === 0 ? 2 : 1),
          drawTicks: true
        },
        border: { display: true, color: 'rgba(255, 255, 255, 0.5)' }
      },
      x: {
        type: 'linear',
        position: 'bottom',
        min: minStrikePrice > 0 ? minStrikePrice : undefined,
        max: maxStrikePrice > 0 ? maxStrikePrice : undefined,
        offset: false,
        ticks: {
          color: 'white',
          font: { size: 10 }
        },
        grid: { color: 'rgba(255, 255, 255, 0.1)' },
        border: { display: true, color: 'rgba(255, 255, 255, 0.5)' }
      }
    },
    plugins: {
      legend: {
        labels: { color: 'white' }
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        axis: 'x',
        filter: function (tooltipItem) {
          return tooltipItem.raw && tooltipItem.raw.y !== 0;
        },
        callbacks: {
          title: function (context) {
            return `Strike: ${context[0].parsed.x}`;
          },
          label: function (context) {
            const accName = context.dataset.label;
            const strike = context.parsed.x;
            // Find all options at this strike/account/type
            const options = activeOptions.filter(opt =>
              opt.value === strike &&
              opt.acct_name === accName &&
              ((barGraphFilter === 'Call' && opt.stype === 'C') || (barGraphFilter === 'Put' && opt.stype === 'P'))
            );

            return options.map(opt => `${opt.type} (${opt.quantity})`).join(', ');
          }
        }
      }
    }
  };

  const custom2DBarPlugin = {
    id: 'custom2DBarPlugin',
    afterDatasetsDraw(chart) {
      const { ctx, tooltip, chartArea: { top, bottom } } = chart;

      // 1. Draw Vertical Line on active hover
      if (tooltip && tooltip.opacity !== 0) {
        const x = tooltip.caretX;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.restore();
      }

      // 2. Draw Bar Labels
      chart.data.datasets.forEach((dataset, i) => {
        const meta = chart.getDatasetMeta(i);
        meta.data.forEach((element, index) => {
          const dataVal = dataset.data[index].y; // Use y value
          if (dataVal !== 0) {
            const elementHeight = Math.abs(element.y - element.base);
            const elementWidth = element.width;

            // Auto hide labels only if they are truly unreadable
            if (elementWidth >= 6 && elementHeight >= 10) {
              ctx.save();
              ctx.fillStyle = 'white';
              ctx.font = '9px Arial';

              const centerY = (element.y + element.base) / 2;
              const centerX = element.x;

              ctx.translate(Math.round(centerX), Math.round(centerY));
              ctx.rotate(-Math.PI / 2);
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';

              const strikeValue = dataset.data[index].x;
              const accName = dataset.label;
              const options = activeOptions.filter(opt =>
                opt.value === strikeValue &&
                opt.acct_name === accName &&
                ((barGraphFilter === 'Call' && opt.stype === 'C') || (barGraphFilter === 'Put' && opt.stype === 'P'))
              );

              const label = options.map(opt => {
                const shortType = opt.type.split(' ').map(w => w[0]).join('');
                return `${shortType} ${strikeValue} (${opt.quantity})`;
              }).join(' | ');

              ctx.fillText(label, 0, 0);
              ctx.restore();
            }
          }
        });
      });
    }
  };

  // 7. JSX RETURN
  return (
    <div style={{ width: '90%', margin: 'auto' }} >

      {loading && <div className="loader">
        <RotatingLines
          strokeColor="grey"
          strokeWidth="5"
          animationDuration="0.75"
          width="96"
          visible={true}
        /></div>
      }

      <Modal show={show} onHide={() => setShow(true)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Login</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleLogin}>
            <Form.Group controlId="formUsername" className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formPassword" className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formRememberMe" className="mb-3">
              <Form.Check
                type="checkbox"
                label="Remember Me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
            </Form.Group>

            {error && <p className="text-danger">{error}</p>}

            <Button variant="primary" type="submit" className="w-100">
              Login
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <h2>Options Payoff Strategy</h2>

      <div className='ds'>
        <div>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>Custom</Typography>
            <Switch checked={portfolioMode} onChange={handlePortfolioSwitch} />
            <Typography>Portfolio</Typography>
          </Stack>
        </div>

        <motion.div
          className="relative w-16 h-8 rounded-full cursor-pointer bg-gray-300 dark:bg-gray-700"
          onClick={() => setIsLocked((prev) => !prev)}
        >
          <motion.div
            className=""
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            {isLocked ? <Lock size={16} /> : <Unlock size={16} />} <span className="text-lg font-semibold">{isLocked ? "Locked" : "Unlocked"}</span>
          </motion.div>
        </motion.div>

        <div className='ds2'>

          {stockPrice ? <div className="UnderlyingDetails_price__left__xitcX"><span style={{ fontWeight: 500 }}>{selectedSymbol}</span> Spot price <span style={{ fontWeight: 500 }}>${stockPrice}</span> </div> : ''}

          <button onClick={() => navigate("/table")} className='btn btn-success tbl-cls'>
            Table
          </button>

          <Dropdown onSelect={handleacct_nameChange} style={{ display: (hideACCName == 'block') ? 'block' : 'none' }}>
            <Dropdown.Toggle variant="success" id="acct_name-dropdown">
              {selectedacct_name ? selectedacct_name : 'Select Account Name'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item key="All Accounts" eventKey="All Accounts" style={{}}>
                All Accounts
              </Dropdown.Item>
              {acct_names.map((acct_name, index) => (
                <Dropdown.Item key={index} eventKey={acct_name}>
                  {acct_name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown onSelect={handleSymbolChange}>
            <Dropdown.Toggle variant="success" id="symbol-dropdown">
              {selectedSymbol ? selectedSymbol : 'Select Stock'}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {symbols.map((symbol, index) => (
                <Dropdown.Item key={index} eventKey={symbol}>
                  {symbol}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown onSelect={handleExpiryChange} style={{ display: (hideExpiry === 'block') ? 'block' : 'none' }}>
            <Dropdown.Toggle variant="success" id="expiry-dropdown">
              {selectedExpiry.length === 1 && selectedExpiry[0] === "No Expiry"
                ? "No Expiry"
                : selectedExpiry.length > 0
                  ? selectedExpiry.filter(expiry => expiry !== "No Expiry").join(", ")
                  : "All Expiry"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item key="All Expiry" eventKey="All Expiry" style={{}}>
                All Expiry
              </Dropdown.Item>
              {expiries.map((expiry, index) => (
                <Dropdown.Item key={index} className={(index === 0) ? 'drop-expiry' : ''} eventKey={expiry} active={selectedExpiry.includes(expiry)} style={{}}>
                  {expiry}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown style={{ display: (hideExpiry == 'block') ? 'block' : 'none' }}>
            <Dropdown.Toggle variant="success" id="dropdown-basic">
              Add Options
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <div className="dropdown-item" style={{ fontWeight: 600 }}>Options:</div>

              {dropdownItems.map((item, idx) => (
                <Dropdown.Item
                  key={idx}
                  onClick={() => handleDropdownSelect(
                    item.type.includes('Buy') ? 0 : 1,
                    item.type
                  )}
                >
                  {item.label} <span style={{ color: item.labelType === 'Call' ? 'Green' : 'Red' }}>{item.labelType}</span>
                </Dropdown.Item>
              ))}

              <Modal className='modal-sm' show={showPopup} onHide={() => setShowPopup(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>Select Expiry Date</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                  <h6>{selectedSymbol} {popupStrikeType}</h6>

                  <Form.Select onChange={handleExpiryChangeOptions} value={optionsSelectedExpiry}>
                    <option value="">Select Expiry</option>
                    {allExpiries.map((expiry, idx) => (
                      <option key={idx} value={expiry}>{expiry}</option>
                    ))}
                  </Form.Select>

                  {optionsSelectedExpiry && (
                    <Form.Select onChange={handleStrikeChange} value={selectedStrike} className="mt-3">
                      <option value="">Select Strike Price</option>
                      {optionsStrikes.map((strikeObj, idx) => (
                        <option key={idx} value={strikeObj.strike}>{strikeObj.strike}</option>
                      ))}
                    </Form.Select>
                  )}

                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowPopup(false)}>
                    Close
                  </Button>
                  <Button variant="primary" onClick={handleAddStrike} disabled={!selectedStrike}>
                    Add Strike
                  </Button>
                </Modal.Footer>
              </Modal>

              <div className="dropdown-item" style={{ fontWeight: 600 }}>Underlying:</div>
              {dropdownItems_underlying.map((item, idx) => (
                <Dropdown.Item
                  key={idx}
                  onClick={() => {
                    addUnderlying(item.type);
                  }}
                >
                  <span style={{ color: (item.type === 'Buy') ? 'Green' : 'Red' }}>{item.type}</span> {selectedSymbol}
                </Dropdown.Item>
              ))}

              <Modal className='modal-sm' show={showPopupUnderlying} onHide={() => setShowPopupUnderlying(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>Enter Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                  <h6>{selectedSymbol} {popupTypeUnderlying}</h6>

                  <Form.Group controlId="sharesInput">
                    <Form.Label>Number of Shares</Form.Label>
                    <Form.Control
                      type="number"
                      value={popupShares}
                      onChange={(e) => setPopupShares(e.target.value)}
                      placeholder="Enter number of shares"
                    />
                  </Form.Group>
                  <Form.Group controlId="avgPriceInput">
                    <Form.Label>Price per Share</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={popupAvgPriceShare}
                      onChange={(e) => setPopupAvgPriceShare(e.target.value)}
                      placeholder="Enter price per share"
                    />
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowPopupUnderlying(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleAddUnderlyingData}>
                    Add
                  </Button>
                </Modal.Footer>
              </Modal>

            </Dropdown.Menu>
          </Dropdown>

          <Dropdown style={{ display: hideExpiry === 'block' ? 'block' : 'none' }}>
            <Dropdown.Toggle variant="success" id="dropdown-basic">
              Filter Options
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {filterDropdownItems.map((item, idx) => {
                const totalQuantity = (strikes || []).reduce((sum, group) => {
                  return (
                    sum +
                    (Array.isArray(group.values)
                      ? group.values.reduce((subSum, strike) => {
                        return strike.type === item.type
                          ? subSum + (strike.quantity || 0)
                          : subSum;
                      }, 0)
                      : 0)
                  );
                }, 0);

                const totalAvgQuantity = (strikes || []).reduce((sum, group) => {
                  return (
                    sum +
                    (Array.isArray(group.values)
                      ? group.values.reduce((subSum, strike) => {
                        return strike.type === item.type
                          ? subSum + (strike.quantity || 0) * (strike.value || 0)
                          : subSum;
                      }, 0)
                      : 0)
                  );
                }, 0);

                const Avg = totalQuantity > 0 ? (totalAvgQuantity / totalQuantity).toFixed(0) : 0;

                return (
                  <Dropdown.Item
                    key={idx}
                    onClick={() =>
                      handleFilterDropdownSelect(
                        item.type.includes('Buy') ? 0 : 1,
                        item.type
                      )
                    }
                  >
                    <Form.Check
                      type="checkbox"
                      id={`filter-checkbox-${idx}`}
                      checked={checkedItems[idx]}
                      onChange={() => handleCheckboxChange(idx)}
                      inline
                    />
                    {item.label}{' '}
                    <span
                      style={{
                        color: item.labelType === 'Call' ? 'Green' : 'Red',
                      }}
                    >
                      {item.labelType}
                    </span>{' '}
                    ({totalQuantity}){' '}
                    ({Avg})
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>

          </Dropdown>

        </div>

      </div>

      <h4>Strikes:</h4>

      <div>
        <ul className='colorShades'>
          {expiries.map((expiry, index) => (
            <li key={index} className={'show-expiry'} eventKey={expiry} >
              <span className={'show-expiry-span'} style={{ backgroundColor: getExpiryColor(expiry) }}></span>{expiry}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <ul className='graphLines'>
          <li className={'show-graphLines'} >
            <span className={'show-graphLines-span-plain'} style={{}}></span>All expiry
          </li>
          <li className={'show-graphLines'} >
            <span className={'show-graphLines-span-dot'} style={{}}></span>Selected expiry
          </li>
          <li className={'show-graphLines'} >
            <span className={'show-graphLines-span-dash'} style={{}}></span>Equity
          </li>
          <li className={'show-graphLines'} >
            <span className={'show-graphLines-span-whitedot'} style={{}}></span>CMP
          </li>
          <li className={'show-graphLines'} >
            <span className={'show-graphLines-span-bluedot'} style={{}}></span>Break Even Point
          </li>
        </ul>
      </div>

      {
        underlyingData ? (
          <div className="StrategyBuilder_control__CvRj4">
            <div className="StrategyBuilder_input__header__Qqk0q">
              {underlyingData.length ? 'Equity:' : ''}
            </div>
            <div className="EquitiesList_equities__kFkji">
              {underlyingData.map((equity, index) => (
                <div
                  key={index}
                  className="EquitiesList_equity__dchK3"
                  onContextMenu={(e) => handleRightClick(e, index)}
                >
                  <strong>{equity.side === 'B' ? 'Long' : 'Short'} {equity.shares}</strong>
                  <span> shares at </span>
                  <strong>${equity.avg_price_share}</strong>
                </div>
              ))}
            </div>
          </div>
        ) : (
          ''
        )
      }

      {
        showContextMenu && underlyingData[currentEditIndex] && (
          <div
            className="ContextMenu_container__Gv9l9 context-menu"
            style={{
              position: 'absolute',
              left: `${contextMenuPosition.x}px`,
              top: `${contextMenuPosition.y}px`,
              minWidth: '280px',
              maxWidth: '300px',
              opacity: 1,
              transition: 'opacity 150ms ease-in-out',
              border: '1px solid #ccc',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              zIndex: 1000
            }}
          >
            <div className="ContextMenu_row__A_hbc ContextMenu_row--header__Ck4t9">
              {editValues.shares}× {underlyingData[currentEditIndex].side === 'B' ? 'Buy' : 'Sell'} {selectedSymbol}
            </div>
            <div className="ContextMenu_row__A_hbc ContextMenu_row--columns__Damcu">
              <div className="ContextMenu_column__8ueQs">
                <div>Quantity</div>
                <div className="PriceAndQuantityMenu_control__BPi17">
                  <strong>
                    <input
                      type="number"
                      step="100"
                      value={editValues.shares}
                      onChange={(e) => setEditValues({ ...editValues, shares: e.target.value })}
                      className="EditableText_text__7BEoU"
                      style={{ width: '50px' }}
                    />
                  </strong>
                </div>
              </div>
              <div className="ContextMenu_column__8ueQs">
                <div>Price</div>
                <div className="PriceAndQuantityMenu_control__BPi17">
                  <strong>
                    $<input
                      type="number"
                      step="0.01"
                      value={editValues.avg_price_share}
                      onChange={(e) => setEditValues({ ...editValues, avg_price_share: e.target.value })}
                      className="EditableText_text__7BEoU"
                      style={{ width: '50px' }}
                    />
                  </strong>
                </div>
              </div>
            </div>
            <div
              className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO"
              role="button"
              onClick={handleSaveChanges}
            >
              <svg
                aria-hidden="true"
                focusable="false"
                data-prefix="far"
                data-icon="check"
                className="svg-inline--fa fa-check"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                style={{ marginRight: '5px' }}
              >
                <path
                  fill="currentColor"
                  d="M441 103c9.4 9.4 9.4 24.6 0 33.9L177 401c-9.4 9.4-24.6 9.4-33.9 0L7 265c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l119 119L407 103c9.4-9.4 24.6-9.4 33.9 0z"
                ></path>
              </svg>
              Save Changes
            </div>

            <div onClick={handleRemoveEquity} className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO" role="button" tabIndex="0">
              <svg
                aria-hidden="true"
                focusable="false"
                data-prefix="far"
                data-icon="xmark"
                className="svg-inline--fa fa-xmark"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 384 512"
                style={{ marginRight: '5px' }}
              >
                <path
                  fill="currentColor"
                  d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"
                ></path>
              </svg>
              Remove </div>
            <div
              className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO"
              role="button"
              onClick={() => setShowContextMenu(false)}
            >
              Close
            </div>
          </div>
        )
      }

      <div className='sliderOver' style={{ padding: `${sliderOverPad}px 0` }}>
        <div className="slider">
          <div id="draggable">
            <div
              id="text-content"
              ref={textContentRef}
              onMouseDown={handleMouseDownslide}
              style={{ position: "relative" }}
            >

              <div className="slider-container" ref={sliderRef} style={{ width: `${screenWidth}px` }}>

                <div className="range-slider">
                  {
                    (min != null && max != null && !isNaN(min) && !isNaN(max) && Math.round(max) > Math.round(min)) ? (

                      Array.from({ length: Math.round(max) - Math.round(min) + 1 }).map((_, index) => {
                        const value = Math.round(min) + index;
                        const percent = ((value - Math.round(min)) / (Math.round(max) - Math.round(min))) * 100;
                        const isMultipleOf5 = value % 5 === 0;
                        const isMultipleOf10 = value % 10 === 0;

                        return (
                          <div
                            key={value}
                            className="slider-label"
                            style={{
                              position: 'absolute',
                              left: `${percent}%`,
                              transform: 'translateX(-50%)',
                              top: isMultipleOf10 ? '-25px' : isMultipleOf5 ? '-20px' : '-15px',
                              fontSize: isMultipleOf10 ? '12px' : isMultipleOf5 ? '10px' : '8px',
                              fontWeight: isMultipleOf10 ? 'bold' : 'normal',
                            }}
                          >
                            {isMultipleOf5 ? (isMultipleOf10 ? value : value) : '|'}
                          </div>
                        );
                      })
                    ) : (
                      <div>Loading...</div>
                    )
                  }

                </div>

                {
                  minStrikePrice > 0 && strikes?.length > 0 && strikes.map((group, groupIndex) => (
                    group.values?.length > 0 && group.values.map((item, valueIndex) => {
                      if (item.value <= 0) return null;

                      const backgroundColor = getExpiryColor(item.expiry);

                      return (
                        <div
                          key={`${groupIndex}-${valueIndex}`}
                          className={`thumb thumb-${groupIndex}-${valueIndex} ${item.stype === 'C' ? 'green-thumb' : 'red-thumb'}`}
                          onMouseDown={handleMouseDown(groupIndex, valueIndex)}
                          onContextMenu={(event) => handleContextMenucus(event, groupIndex, valueIndex)}
                          onClick={closePopup}
                        >
                          <div className={`value-block ${group.type === "Buy" ? 'above-slider' : 'below-slider'}`}>
                            {(item.value)}

                            <span className='strike-below'>
                              {item.stype}
                              {(item.avg_price_share_new === 0) ? (
                                <span className="StrikeSelector_point__icon__km_Ta strike_icon">
                                  <svg
                                    aria-hidden="true"
                                    focusable="false"
                                    data-prefix="fas"
                                    data-icon="triangle-exclamation"
                                    className="svg-inline--fa fa-triangle-exclamation ItemIcons_icon__WDozd ItemIcons_icon--warning__TP2Sz ItemIcons_icon--shadow__6Vgg2"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                  >
                                    <path
                                      fill="currentColor"
                                      d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480L40 480c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24l0 112c0 13.3 10.7 24 24 24s24-10.7 24-24l0-112c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"
                                    ></path>
                                  </svg>
                                </span>
                              ) : ''}
                            </span>
                            <span
                              className='strike-qty'
                              style={{ backgroundColor }}
                            >
                              x{item.quantity}
                            </span>

                            <span className={`${group.type === "Buy" ? 'mark-1' : 'mark-2'}`} style={{ backgroundColor }}>{`${item.acct_name === "Walleyhouse" ? 'W' : 'M'}`}</span>

                          </div>
                        </div>
                      );
                    })
                  ))
                }

              </div>

            </div>
          </div>
        </div>
      </div>

      {
        popupData && (
          <div className="popup" style={{ left: `${popupData.x}px`, top: `${popupData.y}px` }}>

            <div className="ContextMenu_container__Gv9l9 context-menu" style={{}}>
              <div className="ContextMenu_row__A_hbc ContextMenu_row--header__Ck4t9">{selectedSymbol} <span style={{ color: `${popupData.stype === 'C' ? '#00c038' : '#c20028'}` }}>{popupData.value}{popupData.stype}</span> <span style={{ color: 'yellow' }}>{popupData.expiry}</span></div>

              {(popupData.avg_price_share_new === 0) ? <div className="ContextMenu_row__A_hbc">
                <span className="OptionDetailsWarnings_warnings___6mR7"><svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="triangle-exclamation" className="svg-inline--fa fa-triangle-exclamation " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M248.4 84.3c1.6-2.7 4.5-4.3 7.6-4.3s6 1.6 7.6 4.3L461.9 410c1.4 2.3 2.1 4.9 2.1 7.5c0 8-6.5 14.5-14.5 14.5l-387 0c-8 0-14.5-6.5-14.5-14.5c0-2.7 .7-5.3 2.1-7.5L248.4 84.3zm-41-25L9.1 385c-6 9.8-9.1 21-9.1 32.5C0 452 28 480 62.5 480l387 0c34.5 0 62.5-28 62.5-62.5c0-11.5-3.2-22.7-9.1-32.5L304.6 59.3C294.3 42.4 275.9 32 256 32s-38.3 10.4-48.6 27.3zM288 368a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm-8-184c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 96c0 13.3 10.7 24 24 24s24-10.7 24-24l0-96z"></path></svg> Please change the strike price, this stike price is not available on this expiry.</span>
              </div> : ''}

              <div className="ContextMenu_row__A_hbc ContextMenu_row--columns__Damcu">

                <div className="ContextMenu_column__8ueQs">
                  <div>Quantity</div>
                  <div className="PriceAndQuantityMenu_control__BPi17">
                    <span
                      className="icon-button PriceAndQuantityMenu_qty__icon__lbCxZ"
                      role="button"
                      tabIndex="0"
                      onClick={() => handleQuantityChangepop(-1)}
                    >
                      <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-chevron-left" className="svg-inline--fa fa-circle-chevron-left" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path fill="currentColor" d="M512 256A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM271 135c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-87 87 87 87c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L167 273c-9.4-9.4-9.4-24.6 0-33.9L271 135z" />
                      </svg>
                    </span>
                    <strong className="PriceAndQuantityMenu_qty__value__xB0h1">
                      <span data-editable="true" tabIndex="0" role="button" aria-label="Edit" className="EditableText_host__RKtXH">
                        <span className="EditableText_text__7BEoU" tabIndex="0">{popupData.quantity}</span>
                      </span>
                    </strong>
                    <span
                      className="icon-button PriceAndQuantityMenu_qty__icon__lbCxZ"
                      role="button"
                      tabIndex="0"
                      onClick={() => handleQuantityChangepop(1)}
                    >
                      <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-chevron-right" className="svg-inline--fa fa-circle-chevron-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path fill="currentColor" d="M0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zM241 377c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l87-87-87-87c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L345 239c9.4 9.4 9.4 24.6 0 33.9L241 377z" />
                      </svg>
                    </span>
                  </div>
                </div>

                <div className="ContextMenu_column__8ueQs">
                  <div>Price</div>
                  <div className="PriceAndQuantityMenu_control__BPi17">
                    <strong>
                      $<span data-editable="true" tabIndex="0" role="button" aria-label="Edit" className="EditableText_host__RKtXH">
                        <span className="EditableText_text__7BEoU" tabIndex="0">{(thumbMoved) ? popupData.avg_price_share_new : popupData.avg_price_share}</span>
                      </span>
                    </strong>
                  </div>
                </div>
              </div>
              <div className="ContextMenu_row__A_hbc OptionDetails_greeks__iIe26">
                {[
                  { label: "Bid", value: bidprice, color: "green" },
                  { label: "Ask", value: askprice, color: "red" },
                  { label: "Volume", value: volume },
                  { label: "OI", value: oi },
                  { label: "IV", value: iv.toFixed(2) + '%' },
                  { label: "Delta", value: delta },
                  { label: "Theta", value: theta },
                  { label: "Gamma", value: gamma },
                  { label: "Vega", value: vega },
                  { label: "Rho", value: rho },
                ].map((greek, index) => (
                  <div className="OptionDetails_greek__IMSKG" key={index}>
                    <span>{greek.label} </span>
                    <span className={`OptionDetails_greek__value__WLRnm ${greek.color || ""}`}>{greek.value}</span>
                  </div>
                ))}
              </div>

              <div className="ContextMenu_row__A_hbc">
                <label className='expiry-select' htmlFor="expiry-select">Change Expiration</label>
                <select
                  id="expiry-select"
                  value={popupData.expiry || ''}
                  onChange={(e) => handleExpiryChangePopup(e.target.value)}
                  className="expiry-dropdown"
                >
                  {allExpiries.map((expiry, idx) => (
                    <option key={idx} value={expiry}>
                      {expiry}
                    </option>
                  ))}
                </select>
              </div>

              <div onClick={removeSliderValue} className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO" role="button" tabIndex="0">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="far"
                  data-icon="xmark"
                  className="svg-inline--fa fa-xmark"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 384 512"
                  style={{ marginRight: '5px' }}
                >
                  <path
                    fill="currentColor"
                    d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z"
                  ></path>
                </svg>
                Remove </div>
              <div onClick={closePopup} className="ContextMenu_row__A_hbc ContextMenu_row--button__iV4HO" role="button" tabIndex="0">Close </div>

            </div>
          </div>
        )
      }

      <Carousel
        swipeable={true}
        draggable={true}
        showDots={false}
        responsive={responsive}
        ssr={true}
        infinite={true}
        autoPlay={false}
        autoPlaySpeed={1000}
        keyBoardControl={true}
        customTransition="all .5"
        transitionDuration={2000}
        containerClass="carousel-container"
        removeArrowOnDeviceType={["tablet", "mobile"]}
        dotListClass="custom-dot-list-style"
        itemClass="carousel-item-padding-40-px"
      >
        <div>Premium deployed: ${premium_deployed.toFixed(0)}</div>
        <div>Max Profit: ${max_profit.toFixed(1)}</div>
      </Carousel>

      <Line data={chartData} options={chartOptions} />

      <div style={{ marginTop: '60px', borderTop: '1px solid #333', paddingTop: '40px' }}>
        <h4 style={{ textAlign: 'center', color: '#1b9fdf' }}>Strike Payoff (Summarized)</h4>
        <Line data={chartDataAvg} options={chartOptionsAvg} />
      </div>

      <div className='sliderWi' style={{ marginTop: '20px' }}>
        <h4>Adjust Display Range</h4>
        <Slider
          value={sliderRange}
          onChange={handleRangeSliderChange}
          valueLabelDisplay="on"
          valueLabelFormat={(value) => `+-${value}%`}
          min={1}
          max={50}
          marks
          disableSwap
        />
      </div>

      <div className='sliderWi' >
        <h4>Date</h4>
        <Slider
          value={date}
          onChange={handleSliderChange}
          valueLabelDisplay="on"
          valueLabelFormat={(value) => formatDateLabel(value)}
          min={0}
          max={maxDays}
          marks
        />

      </div>

      <div className='d-flex gap10 sliderWi'>
        <div className='slide' style={{ width: '100%' }}>
          <h4>Implied Volatility</h4>
          <Slider
            value={impliedVolatility}
            onChange={(event, newValue) => setImpliedVolatility(newValue)}
            valueLabelDisplay="on"
            valueLabelFormat={(value) => `${(value === 0) ? 'At Respective Volatility' : 'Volatility: ' + value + '%'}`}
            min={0}
            max={100}
            marks
          />
        </div>

        <div className='slide' style={{ width: '100%' }}>
          <h4>Risk Free Rate</h4>
          <Slider
            value={riskFreeRate}
            onChange={(event, newValue) => setriskFreeRate(newValue)}
            valueLabelDisplay="on"
            valueLabelFormat={(value) => `Rate: ${value}%`}
            min={0}
            max={10}
            step={.01}
            marks
          />
        </div>

      </div>

      <div style={{ marginTop: '40px', height: '400px' }}>
        <h4 style={{ textAlign: 'center' }}>Positions (CALL/PUT)</h4>
        <div className="bar-toggle" style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '25px' }}>
          {['Call', 'Put'].map((type) => (
            <button
              key={type}
              onClick={() => setBarGraphFilter(type)}
              style={{
                padding: '8px 24px',
                borderRadius: '20px',
                border: '1px solid #555',
                backgroundColor: barGraphFilter === type ? '#1b9fdf' : '#2a2a2a',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: barGraphFilter === type ? 'bold' : 'normal',
                transition: 'all 0.2s ease',
                minWidth: '100px',
                boxShadow: barGraphFilter === type ? '0 0 10px rgba(27, 159, 223, 0.5)' : 'none'
              }}
            >
              {type}
            </button>
          ))}
        </div>
        <Bar data={barChartData} options={barChartOptions} plugins={[custom2DBarPlugin]} />
      </div>

      {/* <div style={{ marginTop: '60px', height: '400px', borderTop: '1px solid #333', paddingTop: '40px', paddingBottom: '60px' }}>
        <h4 style={{ textAlign: 'center', color: '#1b9fdf' }}>Average Positions (Summarized)</h4>
        <Bar data={barChartDataAvg} options={barChartOptions} plugins={[custom2DBarPlugin]} />
      </div> */}

    </div >
  );
};

export default OptionsGraphWithMultiStrikeSlider;



